import React, { useState } from 'react';
import classNames from 'classnames';

import * as api from '../../api';
import { AppointmentsListDoctorView } from './AppointmentsListDoctorView';
import { Button } from '../../components/Button';
import { CancelAppointmentModal } from './CancelAppointmentModal';
import { InfoMessage } from '../../components/InfoMessage';
import LoadingBar from '../../components/LoadingBar';

import defaultStyles from '../../styles/formSetStyles.module.css';
import styles from './index.module.css';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
  appointments: api.NotificationIntent.Appointment[];
  onAppointmentsReply: (
    appointments: api.NotificationIntent.Appointment[],
    reply: api.EhrTimeSlot.AppointmentStatus,
  ) => void;
  getAppointments: () => Promise<void>;
  pending?: boolean;
  anyReschedulable?: boolean;
  onConfirmed?: () => void;
  skipConfirmationScreen?: boolean;
}
export const RescheduleInfoView = () => {
  const { t } = useTranslation();
  return (
    <InfoMessage>
      {t(
        'To reschedule an appointment, please call the number provided with your appointment above.',
      )}
    </InfoMessage>
  );
};

/**
 * Display a list of appointments that's wait for status confirmation
 */
export const AppointmentsListView: React.FC<Props> = ({
  appointments,
  getAppointments,
  onAppointmentsReply,
  pending,
  anyReschedulable,
  onConfirmed,
  skipConfirmationScreen,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingText, setLoadingText] = useState<React.ReactNode>(
    t('Loading...'),
  );
  const appointmentsCount = appointments.length;
  const hasCancellableAppointment = appointments.some((a) => a.isCancellable);
  const cancelButton =
    anyReschedulable || !hasCancellableAppointment ? null : (
      <Button
        type="button"
        onClick={() => setIsModalOpen(true)}
        children={t('Cancel an appointment')}
        disabled={pending}
        invertColors={true}
      />
    );

  if (pending) {
    return <LoadingBar label={loadingText} />;
  }

  return (
    <>
      <div className={styles.AppointmentsListViewContainer}>
        <CancelAppointmentModal
          isOpen={isModalOpen}
          onModalClose={() => setIsModalOpen(false)}
          appointments={appointments}
          onCancelAppointments={(ids) => {
            onAppointmentsReply(
              appointments.filter((appointment) =>
                ids.includes(appointment.encounter.id),
              ),
              api.EhrTimeSlot.AppointmentStatus.Cancelled,
            );
          }}
          pending={pending}
        />
        <div className={classNames(defaultStyles.Label, styles.Label)}>
          <Trans i18nKey="pleaseConfirmAppointment" count={appointmentsCount} />
        </div>
        <div className={styles.AppointmentsList}>
          {appointments.map((appointment) => (
            <AppointmentsListDoctorView
              key={appointment.encounter.id}
              appointment={appointment}
              pending={pending}
              getAppointments={getAppointments}
              setLoadingText={setLoadingText}
              onAppointmentsReply={onAppointmentsReply}
            />
          ))}
        </div>
        <Button
          type="button"
          onClick={async () => {
            setLoadingText(t('Confirming appointments...'));
            await onAppointmentsReply(
              appointments,
              api.EhrTimeSlot.AppointmentStatus.Confirmed,
            );
            if (skipConfirmationScreen && onConfirmed) {
              onConfirmed();
            }
          }}
          children={<Trans i18nKey="yesConfirm" count={appointmentsCount} />}
          disabled={pending}
        />
        {cancelButton}
        {!anyReschedulable && <RescheduleInfoView />}
      </div>
    </>
  );
};
